import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { UploadSimple, X, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function CreatePost() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [title, setTitle] = useState("");
    const [desc, setDesc] = useState("");
    const [category, setCategory] = useState("");
    const [contact, setContact] = useState(user?.email || "");
    // 2026-02: support up to 4 images (Instagram-style carousel).
    // `imageUrls` is the source of truth; we still submit legacy
    // `image_url` = first entry for backward-compat with older backends.
    const [imageUrls, setImageUrls] = useState([]);
    const MAX_IMAGES = 4;
    const [isPaid, setIsPaid] = useState(false);
    const [price, setPrice] = useState("");
    // Admin-only toggle. When true, the backend stores the post with
    // `author_role="admin"` so it lands on the Home wall (curated) rather
    // than the community Ideas feed. Hidden from non-admin users.
    const [curated, setCurated] = useState(false);
    const isAdmin = user?.role === "admin";
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    }, []);

    const onUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        const remaining = MAX_IMAGES - imageUrls.length;
        if (remaining <= 0) {
            toast.error(`Max ${MAX_IMAGES} images allowed`);
            return;
        }
        const toUpload = files.slice(0, remaining);
        if (files.length > remaining) {
            toast.info(`Only ${remaining} more image${remaining === 1 ? "" : "s"} added (max ${MAX_IMAGES})`);
        }
        setUploading(true);
        const base = process.env.REACT_APP_BACKEND_URL;
        const uploaded = [];
        try {
            for (const f of toUpload) {
                if (f.size > 5 * 1024 * 1024) {
                    toast.error(`${f.name}: max 5MB`);
                    continue;
                }
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/uploads", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                uploaded.push(`${base}${data.url}`);
            }
            if (uploaded.length) {
                setImageUrls((prev) => [...prev, ...uploaded]);
                toast.success(`${uploaded.length} image${uploaded.length === 1 ? "" : "s"} uploaded`);
            }
        } catch {
            toast.error("One or more uploads failed");
        } finally {
            setUploading(false);
            // Allow the same file(s) to be picked again after removal.
            e.target.value = "";
        }
    };

    const removeImage = (idx) => {
        setImageUrls((prev) => prev.filter((_, i) => i !== idx));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!title.trim() || !desc.trim() || !category) {
            toast.error("Please fill all required fields"); return;
        }
        if (isPaid && (!price || parseFloat(price) <= 0)) {
            toast.error("Add an asking price for your for-sale idea"); return;
        }
        if (isPaid && !contact?.trim()) {
            toast.error("Add a contact email so Pro subscribers can reach you"); return;
        }
        setSaving(true);
        try {
            const { data } = await api.post("/posts", {
                title, description: desc, category,
                contact_email: contact || null,
                image_url: imageUrls[0] || null,   // legacy compat
                image_urls: imageUrls.length ? imageUrls : null,
                is_paid: isPaid,
                price: isPaid && price ? parseFloat(price) : null,
                curated: isAdmin ? curated : false,
            });
            toast.success(isAdmin && curated ? "Curated to Home" : "Idea posted!");
            navigate(`/post/${data.id}`);
        } catch (err) {
            const msg = err?.response?.data?.detail || "Could not post";
            toast.error(typeof msg === "string" ? msg : "Could not post");
        } finally { setSaving(false); }
    };

    return (
        <div className="px-4 sm:px-6 lg:px-10 py-8 lg:py-12 max-w-3xl mx-auto" data-testid="create-post-page">
            <header className="mb-8">
                <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#FF8A00] mb-2">Share your spark</div>
                <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter brand-text">Post a startup idea</h1>
                <p className="text-muted-foreground mt-2">Describe a problem you'd love to solve — or a venture you're building.</p>
            </header>

            <form onSubmit={submit} className="space-y-6 rounded-2xl border border-border bg-card p-6 lg:p-8" data-testid="create-post-form">
                <div className="rounded-xl border border-[#FF8A00]/30 bg-[#FF8A00]/5 p-3 text-xs text-muted-foreground" data-testid="local-rules-banner">
                    <b className="text-[#FF8A00]">Heads up:</b> before you act on any idea — yours or someone else's — please check with your local regulators and a qualified adviser to make sure it's legal in your country. See{" "}
                    <a href="/terms" className="text-[#6C63FF] font-semibold">Terms §11</a>.
                </div>
                <div>
                    <Label htmlFor="title">Title (will display bold)</Label>
                    <Input id="title" required value={title} onChange={(e) => setTitle(e.target.value)}
                        placeholder="e.g. AI-powered crop disease detection for smallholder farmers"
                        className="font-display font-bold text-lg" data-testid="post-title-input" />
                </div>

                <div>
                    <Label htmlFor="desc">Description</Label>
                    <Textarea id="desc" required value={desc} onChange={(e) => setDesc(e.target.value)}
                        placeholder="Describe the problem, your proposed solution, target market…"
                        rows={6} data-testid="post-description-input" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Label>Category</Label>
                        <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger data-testid="post-category"><SelectValue placeholder="Pick a category" /></SelectTrigger>
                            <SelectContent className="max-h-72">
                                {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label htmlFor="contact">
                            Contact email <span className="text-muted-foreground text-xs">{isPaid ? "(required for for-sale ideas)" : "(optional)"}</span>
                        </Label>
                        <Input id="contact" type="email" value={contact} onChange={(e) => setContact(e.target.value)}
                            placeholder="founder@email.com" data-testid="post-contact" />
                    </div>
                </div>

                <div>
                    <Label>Cover images (optional, up to {MAX_IMAGES})</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
                        {imageUrls.map((url, i) => (
                            <div key={i} className="relative aspect-square rounded-xl border border-border overflow-hidden bg-muted/30" data-testid={`preview-image-${i}`}>
                                <img src={url} alt={`Cover ${i + 1}`} className="w-full h-full object-cover" />
                                <button
                                    type="button"
                                    onClick={() => removeImage(i)}
                                    className="absolute top-1.5 right-1.5 bg-black/70 hover:bg-black text-white rounded-full p-1"
                                    aria-label={`Remove image ${i + 1}`}
                                    data-testid={`remove-image-${i}`}
                                >
                                    <X size={12} />
                                </button>
                                {i === 0 && imageUrls.length > 1 && (
                                    <div className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded bg-black/70 text-white text-[9px] font-bold uppercase tracking-wider">
                                        Cover
                                    </div>
                                )}
                            </div>
                        ))}
                        {imageUrls.length < MAX_IMAGES && (
                            <label className="flex flex-col items-center justify-center aspect-square border border-dashed border-border rounded-xl cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 transition-colors gap-1" data-testid="upload-image-label">
                                <UploadSimple size={22} weight="duotone" />
                                <span className="text-[11px] text-muted-foreground text-center px-2 leading-tight">
                                    {uploading ? "Uploading…" : `Add image (${imageUrls.length}/${MAX_IMAGES})`}
                                </span>
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    className="hidden"
                                    onChange={onUpload}
                                    data-testid="upload-image-input"
                                />
                            </label>
                        )}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1.5">
                        Up to {MAX_IMAGES} images, 5MB each. First one becomes the cover.
                    </p>
                </div>

                <div className="flex items-center justify-between rounded-xl border border-border p-4">
                    <div>
                        <Label className="text-sm font-semibold">Sell this idea?</Label>
                        <p className="text-xs text-muted-foreground">When enabled, your contact email is shown only to Pro subscribers — qualified buyers only.</p>
                    </div>
                    <Switch checked={isPaid} onCheckedChange={setIsPaid} data-testid="post-paid-toggle" />
                </div>

                {isAdmin && (
                    <div className="flex items-center justify-between rounded-xl border-2 border-[#6C63FF]/30 bg-[#6C63FF]/5 p-4" data-testid="post-curated-row">
                        <div>
                            <Label className="text-sm font-semibold flex items-center gap-2">
                                <Sparkle size={14} weight="duotone" className="text-[#6C63FF]" />
                                Curate to Home
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#6C63FF] text-white font-bold tracking-wider uppercase">Admin</span>
                            </Label>
                            <p className="text-xs text-muted-foreground mt-1">
                                When on, this idea appears on the curated <b>Home</b> wall under its category (paywalled like other curated ideas).
                                When off, it goes to the public <b>Feed</b> community wall.
                            </p>
                        </div>
                        <Switch checked={curated} onCheckedChange={setCurated} data-testid="post-curated-toggle" />
                    </div>
                )}

                {isPaid && (
                    <div>
                        <Label htmlFor="price">Asking price ({user?.country ? `for ${user.country}` : "in your local currency"})</Label>
                        <Input id="price" type="number" min="0" step="0.01" value={price}
                            onChange={(e) => setPrice(e.target.value)} placeholder="e.g. 1999"
                            data-testid="post-price" />
                        <p className="text-xs text-muted-foreground mt-1">
                            Shown on your idea card as a "For sale · {price ? "₹/$/£" : "₹/$/£"}{price || "…"}" badge so buyers know your asking price up front.
                        </p>
                    </div>
                )}

                <div className="flex justify-end gap-3">
                    <Button type="button" variant="ghost" onClick={() => navigate(-1)} data-testid="post-cancel">Cancel</Button>
                    <Button type="submit" className="brand-button" disabled={saving} data-testid="post-submit">
                        <Sparkle size={16} weight="fill" className="mr-1" /> {saving ? "Posting…" : "Publish idea"}
                    </Button>
                </div>
            </form>
        </div>
    );
}
